import { Application, BitmapFont, Loader, SCALE_MODES, settings } from "pixi.js"
import { Audio } from "./audio"
import { Client } from "./client"
import { ClientGameLogic } from "./client_game_logic"
import { SCALER } from "./client_util"
import { lpcSpriteSheets } from "./lpc-spritesheets"
import { logger } from "./logger"
import { Keyboard } from "./rendering/impl/pixijs/input/keyboard"
import { Mouse } from "./rendering/impl/pixijs/input/mouse"
import { resolveConfig } from "./runtime_config"
import { PixiJsRenderingFactory } from "./rendering/impl/pixijs/pixijs_rendering_factory"
import { spriteSheetLoader } from "./rendering/impl/pixijs/sprites/spritesheet_loader"

settings.SCALE_MODE = SCALE_MODES.NEAREST
settings.RENDER_OPTIONS.antialias = false

// Every asset url in the client is written relative ("assets/foo.png"), which resolves against
// whatever origin served the page. That is right for a browser tab and wrong for the native
// shell, whose origin is the app bundle -- the art is not shipped inside it. Setting the loader's
// baseUrl once redirects all 50-odd call sites without touching any of them; it is "" everywhere
// else, and resource-loader leaves urls alone when it is empty. Must happen before the first
// Loader.shared.add(), which is why it sits at module top rather than inside the boot callback.
Loader.shared.baseUrl = resolveConfig().assetBase

// The fixed virtual screen the whole client is laid out in: pixi's own default, which the game was
// built on back when the renderer was never resized. Every GUI coordinate, and BOTTOM in
// gui_manager, is written against it.
const VIRTUAL_W = 800
const VIRTUAL_H = 600

// The shape the canvas is letterboxed to. `stage.scale.y = SCALER` stretches the virtual screen
// vertically, and this ratio is exactly what squashes it back, so a circle drawn in stage
// coordinates comes out round. Letterboxing to 16:9 (ScreenDimensions) instead left the whole game
// 2.6% wider than tall -- SCALER is 1.3 where undoing a 4:3 buffer at 16:9 needs 4/3.
const DISPLAY_RATIO = VIRTUAL_W / (VIRTUAL_H / SCALER)

// Ceiling on the drawing buffer, as a resolution multiplier: VIRTUAL_W x VIRTUAL_H x this^2 pixels,
// i.e. ~5M. A fullscreen 4K panel would otherwise ask for ~10M, four times what the client used to
// draw, on hardware that may be an integrated GPU. Past the cap the browser stretches the last
// little bit, which is still far less resampling than before.
const MAX_RESOLUTION = Math.sqrt(5_000_000 / (VIRTUAL_W * VIRTUAL_H))

const foo = setInterval(() => {
    if (window["__GO__"]) {
        clearInterval(foo)
        const stopLoading = window["stopLoading"]
        const app = new Application({
            view: document.getElementById("pixi-canvas") as HTMLCanvasElement,
            resolution: window.devicePixelRatio || 2,
            autoDensity: true,
            backgroundColor: 0x000000,
        })

        Keyboard.initialize()
        Mouse.initialize(app)

        const onLoadComplete = () => {
            if (stopLoading) {
                stopLoading()
            }
            function resize() {
                const width = window.innerWidth
                const height = window.innerHeight
                const letterboxed = width / height >= DISPLAY_RATIO
                const w = letterboxed ? height * DISPLAY_RATIO : width
                const h = letterboxed ? height : width / DISPLAY_RATIO

                // Draw at the density the canvas is actually SHOWN at. The virtual screen is fixed,
                // so this changes nothing about coordinates -- only how many real pixels each
                // virtual unit gets. Without it pixi filled its default 1600x1200 buffer and left
                // the browser to stretch that ~1.8x across the window, which is what made text and
                // HUD art soft. Capped, because the buffer would otherwise grow with the player's
                // monitor and the client has to stay playable on an integrated GPU.
                const dpr = window.devicePixelRatio || 1
                const resolution = Math.max(1, Math.min((w * dpr) / VIRTUAL_W, MAX_RESOLUTION))
                app.renderer.resolution = resolution
                // InteractionManager copies the resolution once, when it is constructed, and never
                // looks at it again. Leave it stale and every interactive widget -- and the mouse
                // position the world is aimed with -- hit-tests in the wrong place.
                const interaction = (app.renderer as any).plugins?.interaction
                if (interaction) {
                    interaction.resolution = resolution
                }
                app.renderer.resize(VIRTUAL_W, VIRTUAL_H)

                // resize() with autoDensity has just written the VIRTUAL size into the canvas
                // style; the letterboxed size is what belongs on screen.
                app.renderer.view.style.width = w + "px"
                app.renderer.view.style.height = h + "px"

                app.stage.scale.y = SCALER
            }

            resize()

            window.onresize = resize

            const factory: PixiJsRenderingFactory = new PixiJsRenderingFactory(app)
            const clientGameLogic = new ClientGameLogic(factory)
            const client: Client = new Client(clientGameLogic)

            // Setup viewport culling debug tools
            import("./debug_viewport_culling").then(({ setupViewportCullingDebug }) => {
                setupViewportCullingDebug(clientGameLogic.clientRenderer)
            })

            const urlSearchParams = new URLSearchParams(window.location.search)
            const params = Object.fromEntries(urlSearchParams.entries())
            if (window["IS_ITCH"] || resolveConfig().autoGuest) {
                client.connect("", "", true)
            } else {
                const { accessToken, guest } = params

                if (accessToken) {
                    client.connect("", accessToken)
                } else {
                    const isGuest = guest === "true"
                    const cachedPlayerName = isGuest ? "" : localStorage.getItem("_playerName_") || ""
                    client.connect(cachedPlayerName, "", isGuest)
                }
            }

            // ONE thrown exception out of a frame must not end the game.
            //
            // pixi's ticker re-arms its requestAnimationFrame AFTER emitting, so a listener that
            // throws stops the loop for good: the canvas holds its last frame, the socket stays up,
            // and the player is left looking at a picture. That is the "my screen froze" report, and
            // it is indistinguishable from a crash however small the thing that threw -- a widget
            // animating a panel, a sprite whose paperdoll was swapped under it, a mask on a destroyed
            // display object. Nothing drawn in a single frame is worth the whole renderer, so: keep
            // drawing, and make sure the first few failures carry a stack somebody can act on.
            //
            // The count AND the stacks go on `window` deliberately. On a device there is no console
            // to scroll back through, and even in a browser the console is no use here: this client
            // logs a couple of thousand texture warnings on load, so by the time a frame fails its
            // stack is somewhere in the middle of that and cannot be found. `__frameErrors` answers
            // "has this client been dropping frames", and `__frameErrorLog` says what it was --
            // readable over CDP, from devtools, or by a test.
            let frameErrors = 0
            const frameErrorLog: string[] = []
            ;(window as any).__frameErrors = 0
            ;(window as any).__frameErrorLog = frameErrorLog
            app.ticker.add(() => {
                try {
                    client.update()
                } catch (error) {
                    frameErrors++
                    ;(window as any).__frameErrors = frameErrors
                    // The first few only: a frame that fails tends to fail every frame, and the
                    // hundredth copy of one stack is worth less than the first copy of the next.
                    if (frameErrors <= 5) {
                        const detail = (error as Error)?.stack || String(error)
                        frameErrorLog.push(detail)
                        console.error("[frame] update failed", error)
                        logger.error({ tags: ["frame", "error"] }, `frame update failed: ${detail}`)
                    }
                }
            })

            // Expose shadow debug features to global scope for debugging
            if (typeof window !== "undefined") {
                // The scene graph, under the name the PixiJS devtools extension looks for. Also
                // the only way to inspect the render tree on a device, where there is no console
                // to poke at -- attach over CDP and walk app.stage from here.
                ;(window as any).__PIXI_APP__ = app
                // Expose logger to global scope for debugging
                ;(window as any).logger = logger
                ;(window as any).shadowDebug = {
                    getMetrics: () => client.getShadowPerformanceMetrics(),
                    forceUpdate: () => client.forceShadowTextureUpdate(),
                    logMetrics: () => {
                        const metrics = client.getShadowPerformanceMetrics()
                        logger.info({ tags: ["shadow", "performance"] }, "Shadow Performance:", metrics)
                    },
                    debugLog: () => {
                        // Access the shadow rendering directly for debug logging
                        const renderer = client.clientGameLogic.clientRenderer as any
                        if (renderer.shadowRendering) {
                            renderer.shadowRendering.debugLog()
                        }
                    },
                    enableDebugLogging: () => {
                        // Enable debug logging for hash calculation
                        const renderer = client.clientGameLogic.clientRenderer as any
                        if (renderer.shadowRendering) {
                            renderer.shadowRendering.debugHashCalculation = true
                            logger.info({ tags: ["shadow", "debug"] }, "Shadow hash debug logging enabled")
                        }
                    },
                    disableDebugLogging: () => {
                        // Disable debug logging
                        const renderer = client.clientGameLogic.clientRenderer as any
                        if (renderer.shadowRendering) {
                            renderer.shadowRendering.debugHashCalculation = false
                            logger.info({ tags: ["shadow", "debug"] }, "Shadow hash debug logging disabled")
                        }
                    },
                }
                logger.info({ tags: ["shadow", "debug"] }, "Shadow debug tools available at window.shadowDebug")
                logger.info({ tags: ["shadow", "debug"] }, "Shadow debug test available at window.shadowDebugTest")
                logger.info({ tags: ["logger", "debug"] }, "Logger available at window.logger for debugging")

                // Add debug test
                ;(window as any).shadowDebugTest = {
                    startMonitoring: () => {
                        const { ShadowDebugTest } = require("./rendering/impl/pixijs/shadow_debug_test")
                        const debugTest = new ShadowDebugTest(clientGameLogic)
                        debugTest.startMonitoring()
                    },
                    testStationary: () => {
                        const { ShadowDebugTest } = require("./rendering/impl/pixijs/shadow_debug_test")
                        const debugTest = new ShadowDebugTest(clientGameLogic)
                        debugTest.testStationary()
                    },
                    testMovement: () => {
                        const { ShadowDebugTest } = require("./rendering/impl/pixijs/shadow_debug_test")
                        const debugTest = new ShadowDebugTest(clientGameLogic)
                        debugTest.testMovement()
                    },
                }
            }
        }

        loadAssets()

        const aggressiveLoad = false
        Loader.shared.load(
            aggressiveLoad
                ? () =>
                      spriteSheetLoader.bulkLoad(
                          lpcSpriteSheets.map(sheetId => `assets/${sheetId}`),
                          onLoadComplete,
                      )
                : onLoadComplete,
        )
    }
}, 1000)

const loadAssets = () => {
    //
    // fonts
    //
    const chars = {
        chars: [["a", "z"], ["0", "9"], ["A", "Z"], " .!?|,\":<>~!@#$%^&*()_+-=/\\'ë"],
    }

    BitmapFont.from(
        "LocationFont",
        {
            fill: "#00FF00",
            fontSize: 25,
        },
        chars,
    )

    BitmapFont.from(
        "BannerFont",
        {
            fill: "#FFFFFF",
            fontSize: 20,
        },
        chars,
    )

    BitmapFont.from(
        "ImportantFont",
        {
            fill: "#FFFFFF",
            fontSize: 30,
        },
        chars,
    )

    BitmapFont.from(
        "LogoFont",
        {
            fill: "#FFFFFF",
            fontSize: 40,
        },
        chars,
    )

    BitmapFont.from(
        "ScoreFont",
        {
            fill: "#FFFFFF",
            fontSize: 15,
            fontFamily: "Serif",
        },
        chars,
    )

    BitmapFont.from(
        "NameFont",
        {
            fill: "#FFFFFF",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "ChatFont",
        {
            fill: "#00EE00",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "RedChatFont",
        {
            fill: "#FFA500",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "SmallFont",
        {
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "SignFont",
        {
            fill: "#000000",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "ChatBubbleFont",
        {
            fill: "#FFFFFF",
            fontSize: 15,
        },
        chars,
    )

    BitmapFont.from(
        "BlinkFont",
        {
            fill: "#00FF00",
            fontSize: 10,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "ChatHistoryFont",
        {
            fill: "#00FF00",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "LeaderboardFont",
        {
            fill: "#00FF00",
            fontSize: 15,
        },
        chars,
    )

    BitmapFont.from(
        "LeaderboardLeaderFont",
        {
            fill: "#FFFFFF",
            fontSize: 15,
        },
        chars,
    )

    BitmapFont.from(
        "HpFont",
        {
            fill: "#FFFFFF",
            fontSize: 12,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "IdFont",
        {
            fill: "#00FF00",
            fontSize: 12,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "Normal-Green-Bold",
        {
            fill: "#00FF00",
            fontSize: 15,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "TinyFont",
        {
            fill: "#FFFFFF",
            fontSize: 9,
        },
        chars,
    )

    //
    // audio
    //
    Audio.loadAssets()

    Loader.shared
        .add("hit1", "assets/strike1.ogg")
        .add("hit2", "assets/strike2.ogg")
        .add("woodhit", "assets/woodhit.ogg")
        .add("bowshot", "assets/shoot.ogg")
        .add("swordswing", "assets/swordswing.ogg")
        .add("explosion", "assets/explosion.mp3")
        .add("emp", "assets/emp.mp3")
        .add("health", "assets/health.mp3")
        .add("mana", "assets/mana.mp3")
        .add("door_open", "assets/door_open.ogg")
        .add("door_close", "assets/door_close.ogg")
        .add("clocktick", "assets/clocktick.mp3")
        .add("powerup", "assets/powerup.mp3")
        .add("map_location", "assets/map_location.ogg")
        .add("click", "assets/click.mp3")
        .add("coins", "assets/coin3.ogg")
        .add("cloth", "assets/cloth.ogg")
        // procedurally synthesized (packages/tools/src/sfx/gen.py)
        .add("campfire_lit", "assets/campfire_lit.ogg")
        .add("campfire_fed", "assets/campfire_fed.ogg")
        .add("wheel_select", "assets/wheel_select.ogg")
        .add("item_crafted", "assets/item_crafted.ogg")
        .add("wave_starting", "assets/wave_starting.ogg")

    //
    // image
    //
    Loader.shared
        .add("furniture-blonde-wood", "assets/furniture-blonde-wood.json")
        .add("fence_medieval", "assets/fence_medieval.json")
        .add("container", "assets/container.json")
        .add("walls", "assets/walls.json")
        .add("walls2", "assets/walls2.json")
        .add("floors", "assets/floors.json")
        .add("cottage", "assets/cottage.json")
        .add("roofs", "assets/roofs.json")
        .add("ore", "assets/ore.json")
        .add("decorations-medieval", "assets/decorations-medieval.json")
        .add("jungle-ruins", "assets/jungle-ruins.json")
        .add("mountains-v6", "assets/mountains-v6.json")
        .add("lpc-flowers-plants-fungi-wood", "assets/lpc-flowers-plants-fungi-wood.json")
        .add("lpc-terrain", "assets/lpc-terrain.json")
        .add("beach-desert", "assets/beach-desert.json")
        .add("map-atlas", "assets/map-atlas.json")
        .add("plants", "assets/plants.json")
        .add("build_atlas", "assets/build_atlas.json")
        .add("humanoid", "assets/humanoid.json")
        .add("outer", "assets/joystick.png")
        .add("inner", "assets/joystick-handle.png")
        .add("reddit", "assets/reddit.png")
        .add("discord", "assets/discord.png")
        .add("intro_scene", "assets/intro/scene.png")
}
